/**
 * Trick Shot & Combo Scoring — constants and definitions.
 */

import type { StyleGrade, TrickTag } from '../types';

// ─── Trick Tag Definitions ──────────────────────────────────────

export const TRICK_TAGS = {
  DIRECT_HIT:  { label: 'DIRECT HIT',  multiplier: 1.5, color: 0xffcc00 },
  BANK_SHOT:   { label: 'BANK SHOT',   multiplier: 1.8, color: 0x00ccff },
  RICOCHET:    { label: 'RICOCHET',     multiplier: 2.2, color: 0x00ffcc },
  TRICK_SHOT:  { label: 'TRICK SHOT',   multiplier: 3.0, color: 0xff00ff },
  JUGGLE:      { label: 'JUGGLE',       multiplier: 1.5, color: 0xff8800 },
  OVERKILL:    { label: 'OVERKILL',     multiplier: 2.0, color: 0xff0044 },
  HEADWIND:    { label: 'HEADWIND',     multiplier: 1.3, color: 0x88aaff },
  GALE_SHOT:   { label: 'GALE SHOT',   multiplier: 1.5, color: 0x6699ff },
  HURRICANE:   { label: 'HURRICANE',    multiplier: 2.5, color: 0x4466ff },
  TAILWIND:    { label: 'TAILWIND',     multiplier: 1.2, color: 0xaaccff },
  VOID_DUNK:   { label: 'VOID DUNK',    multiplier: 2.5, color: 0xaa00ff },
  LAUNCHED:    { label: 'LAUNCHED',      multiplier: 2.5, color: 0x88ddff },
  EXECUTION:   { label: 'EXECUTION',    multiplier: 2.0, color: 0xff0000 },
  REVENGE:     { label: 'REVENGE',       multiplier: 1.5, color: 0xff4400 },
  CLUTCH:      { label: 'CLUTCH',        multiplier: 1.5, color: 0xffdd00 },
  SNIPER:      { label: 'SNIPER',        multiplier: 2.0, color: 0x44ff44 },
  CROSS_MAP:   { label: 'CROSS MAP',     multiplier: 3.0, color: 0x00ff88 },
  EDGEGUARD:   { label: 'FOLLOW-UP!',   multiplier: 1.8, color: 0x00ffff },
  // ── Void proximity ──
  VOID_EDGE:   { label: 'VOID EDGE',   multiplier: 1.5, color: 0xcc44ff },
  ABYSS_SHOT:  { label: 'ABYSS SHOT',  multiplier: 2.0, color: 0x9900ff },
  VOID_SNIPE:  { label: 'VOID SNIPE',  multiplier: 2.2, color: 0xdd00ff },
  // ── Sky proximity ──
  SKY_HIGH:    { label: 'SKY HIGH',    multiplier: 1.5, color: 0x88ddff },
  ORBIT_SHOT:  { label: 'ORBIT SHOT',  multiplier: 2.0, color: 0x44bbff },
  DEATH_WISH:  { label: 'DEATH WISH',  multiplier: 2.2, color: 0x00eeff },
  // ── Airborne / momentum ──
  AERIAL:      { label: 'AERIAL',      multiplier: 1.3, color: 0x66ddff },
  FADEAWAY:    { label: 'FADEAWAY',     multiplier: 1.5, color: 0xffaa44 },
  FULL_SEND:   { label: 'FULL SEND',   multiplier: 1.5, color: 0xff6644 },
  NO_SCOPE:    { label: 'NO SCOPE',    multiplier: 2.0, color: 0xff44ff },
} as const satisfies Record<string, TrickTag>;

// ─── Style Grade Thresholds ─────────────────────────────────────

export const GRADE_THRESHOLDS: { grade: StyleGrade; minScore: number }[] = [
  { grade: 'S', minScore: 25000 },
  { grade: 'A', minScore: 12000 },
  { grade: 'B', minScore: 3000 },
  { grade: 'C', minScore: 1 },    // any damage dealt
];
// D = miss (0 hits) — no entry needed, it's the fallback

export const GRADE_COLORS: Record<StyleGrade, number> = {
  S: 0xffd700,
  A: 0xff4400,
  B: 0x00ccff,
  C: 0x888888,
  D: 0x555555,
};

export const GRADE_FONT_SIZES: Record<StyleGrade, number> = {
  S: 72,
  A: 56,
  B: 36,
  C: 0,  // silent
  D: 0,  // handled by miss system
};

// ─── Wind Tricks ────────────────────────────────────────────────

export const TRICK_WIND = {
  /** Minimum |wind| for any wind trick to fire. */
  MIN_WIND: 0.03,
  /** |wind| threshold for GALE SHOT (strong wind, any direction). */
  GALE_THRESHOLD: 0.10,
  /** |wind| threshold for HURRICANE (near-max wind + headwind). */
  HURRICANE_THRESHOLD: 0.13,
};

// ─── Void Proximity Tricks ───────────────────────────────────────

export const TRICK_VOID = {
  /** px above voidY for VOID EDGE (target near the abyss). */
  EDGE_DISTANCE: 120,
  /** px above voidY for ABYSS SHOT (attacker firing from near void). */
  ABYSS_DISTANCE: 100,
  /** VOID SNIPE: attacker near void AND target near void. Both thresholds met. */
};

// ─── Airborne / Momentum Tricks ─────────────────────────────────

export const TRICK_MOMENTUM = {
  /** Minimum |vx| for FADEAWAY or FULL SEND to trigger. */
  MIN_SPEED: 1.5,
  /** |vx| threshold for NO SCOPE (airborne + fast). */
  FAST_SPEED: 3.0,
};

// ─── Sky Proximity Tricks ───────────────────────────────────────

export const TRICK_SKY = {
  /** px below upper blast zone for SKY HIGH (target near ceiling). */
  HIGH_DISTANCE: 200,
  /** px below upper blast zone for ORBIT SHOT (attacker firing from near ceiling). */
  ORBIT_DISTANCE: 150,
  /** DEATH WISH: attacker near ceiling AND target near ceiling. Both thresholds met. */
};

// ─── Distance Tricks ────────────────────────────────────────────

export const TRICK_DISTANCE = {
  SNIPER_FRACTION: 0.60,     // > 60% stage width
  CROSS_MAP_FRACTION: 0.85,  // > 85% stage width
};

// ─── Miss Taunting ──────────────────────────────────────────────

export const MISS_TAUNTS = {
  WHIFF_COLOR: 0x666666,
  WHIFF_FONT_SIZE: 24,
  BOO_COLOR: 0xff3333,
  BOO_FONT_SIZE: 36,
  FUNNY_COLOR: 0xff8800,
  FUNNY_FONT_SIZE: 42,
};

export const FUNNY_MISS_NAMES = [
  'STORMTROOPER',
  'POTATO AIM',
  'AIM.EXE NOT FOUND',
  'BUTTERFINGERS',
  'CERTIFIED BRICK',
  'NEEDS GLASSES',
  'THE WHIFFMASTER',
  'NICE TRY CHAMP',
  'SHOOTING BLANKS',
  'WRONG GAME',
  'PANIC FIRE',
  'ARM LIKE A NOODLE',
  'TOTAL AIRBALL',
  'NOT EVEN CLOSE',
  'NEEDS A MAP',
  'THE BLIND BOMBER',
];

// ─── Combo Display ──────────────────────────────────────────────

export const COMBO = {
  COLOR: 0xffcc00,
  FONT_SIZE: 16,
  MIN_FOR_DISPLAY: 2,  // only show combo text at 2+
};

// ─── Trick Tag Display ─────────────────────────────────────────

export const TRICK_DISPLAY = {
  TAG_FONT_SIZE: 20,
  TAG_HOLD: 0.6,   // seconds
  KILL_FONT_SIZE: 28,
  KILL_HOLD: 0.8,
  // End-of-turn sequence timing (delays in seconds)
  SEQ_STEP: 0.5,      // time between each line in the sequence
  SEQ_HOLD: 0.6,      // how long each line stays visible
};

// ─── Text Variants ──────────────────────────────────────────────
// Each trick tag, headline, flavor, and miss taunt has multiple text
// options. Picked deterministically via frameCount so both multiplayer
// clients show the same text without extra sync.

/** Per-tag label variants. Keyed by the tag's base label. */
export const LABEL_VARIANTS: Record<string, string[]> = {
  // ── Per-hit tricks ──
  'DIRECT HIT':  ['DIRECT HIT', 'BULLSEYE', 'NAILED IT', 'CLEAN HIT', 'DEAD CENTER', 'PINPOINT', 'RIGHT ON TARGET', 'MONEY SHOT'],
  'BANK SHOT':   ['BANK SHOT', 'OFF THE WALL', 'CORNER POCKET', 'CALCULATED', 'GEOMETRY', 'ONE CUSHION'],
  'RICOCHET':    ['RICOCHET', 'DOUBLE BOUNCE', 'PING PONG', 'TWO-CUSHION', 'WALL TO WALL', 'REBOUND'],
  'TRICK SHOT':  ['TRICK SHOT', 'SHOWOFF', 'IMPOSSIBLE', 'ARE YOU KIDDING', 'HIGHLIGHT REEL', 'WIZARD SHOT'],
  'JUGGLE':      ['JUGGLE', 'AIR COMBO', 'AIRBORNE', 'HANG TIME', 'SKY HIT', 'MID-AIR'],
  'OVERKILL':    ['OVERKILL', 'BRUTAL', 'DEVASTATING', 'OBLITERATED', 'EXCESSIVE FORCE', 'ABSOLUTE UNIT'],
  'HEADWIND':    ['HEADWIND', 'AGAINST THE WIND', 'WIND DEFIED', 'UPHILL BATTLE', 'FIGHTING THE BREEZE'],
  'GALE SHOT':   ['GALE SHOT', 'STORM SHOT', 'GALE FORCE', 'WINDY', 'ROUGH CONDITIONS', 'WEATHERED'],
  'HURRICANE':   ['HURRICANE', 'TEMPEST', 'STORM RIDER', 'DEFIED THE STORM', 'WIND MASTER', 'CYCLONE'],
  'TAILWIND':    ['TAILWIND', 'WIND ASSISTED', 'RIDING THE WIND', 'WIND AT YOUR BACK', 'BOOSTED'],
  // ── Edgeguard ──
  'FOLLOW-UP!':  ['FOLLOW-UP!', 'ENCORE!', 'ONE MORE!', 'PURSUIT!', 'RELENTLESS!'],
  // ── Kill finishers ──
  'VOID DUNK':   ['VOID DUNK', 'INTO THE ABYSS', 'SWALLOWED', 'CONSUMED', 'BOTTOMLESS', 'DOWN YOU GO'],
  'LAUNCHED':    ['LAUNCHED', 'ORBITAL', 'TO THE MOON', 'BLASTOFF', 'STRATOSPHERE', 'SEE YA'],
  'EXECUTION':   ['EXECUTION', 'FINISHED', 'FATALITY', 'DELETED', 'PUT DOWN', 'GOODNIGHT'],
  'REVENGE':     ['REVENGE', 'PAYBACK', 'KARMA', 'EYE FOR AN EYE', 'SETTLING SCORES'],
  'CLUTCH':      ['CLUTCH', 'AGAINST ALL ODDS', 'DESPERATION', 'LAST STAND', 'SURVIVAL MODE'],
  // ── Void proximity ──
  'VOID EDGE':   ['VOID EDGE', 'CLIFFHANGER', 'BRINK', 'ON THE EDGE', 'TEETERING', 'DANGER ZONE'],
  'ABYSS SHOT':  ['ABYSS SHOT', 'DAREDEVIL', 'LIVING DANGEROUSLY', 'BOLD MOVE', 'NO FEAR', 'ABYSS WALKER'],
  'VOID SNIPE':  ['VOID SNIPE', 'ABYSS TO ABYSS', 'VOID MASTER', 'EDGE LORD', 'DEATH WISH', 'MUTUAL DESTRUCTION'],
  // ── Sky proximity ──
  'SKY HIGH':    ['SKY HIGH', 'NOSEBLEED', 'THIN AIR', 'ALTITUDE', 'CEILING SHOT', 'UP THERE'],
  'ORBIT SHOT':  ['ORBIT SHOT', 'STRATOSPHERE', 'ZERO G', 'SPACE SHOT', 'ASTRONAUT', 'ICARUS'],
  'DEATH WISH':  ['DEATH WISH', 'LIVING ON THE EDGE', 'NO SAFETY NET', 'BOTH EXPOSED', 'RECKLESS', 'MUTUAL RISK'],
  // ── Airborne / momentum ──
  'AERIAL':      ['AERIAL', 'MID-AIR STRIKE', 'AIR SHOT', 'FLYING HIGH', 'HANG TIME SHOT', 'LEVITATING'],
  'FADEAWAY':    ['FADEAWAY', 'KOBE', 'STEP BACK', 'FALLING AWAY', 'DRIFTING', 'PULL-UP'],
  'FULL SEND':   ['FULL SEND', 'CHARGE SHOT', 'RUNNING GUN', 'DRIVE-BY', 'RUSH HOUR', 'BLITZ'],
  'NO SCOPE':    ['NO SCOPE', 'TRICKSHOT', 'FREESTYLE', 'MID-AIR MADNESS', 'FLYING CIRCUS', 'ACROBAT'],
};

/** Headline variants per quality tier. */
export const HEADLINE_VARIANTS = {
  s_grade:    ['Massive Combo.', 'Legendary.', 'Godlike.', 'Unbelievable.', 'Inhuman.', 'Are You Serious.'],
  huge_combo: ['Huge Combo.', 'Monster Combo.', 'Unstoppable.', 'Rampage.', 'On Fire.', 'Relentless.'],
  nice_combo: ['Nice Combo.', 'Combo!', 'Racking Up.', 'Keep Going.', 'On A Roll.', 'Stacking.'],
  sick_shot:  ['Sick Shot.', 'Stylish.', 'Smooth Move.', 'Flashy.', 'Show Off.', 'Technical.'],
  nice_shot:  ['Nice Shot.', 'Good Hit.', "That'll Work.", 'Solid.', 'Clean.', 'Decent Shot.'],
} as const;

/** Flavor text variants per grade. */
export const FLAVOR_VARIANTS: Record<StyleGrade, string[]> = {
  S: ["You're Elite.", 'Legendary Play.', 'Hall of Fame.', 'Untouchable.', 'Peak Performance.'],
  A: ['Impressive.', 'Clean Work.', 'Respect.', 'Stylish Play.', 'Getting Dangerous.'],
  B: ['Not Bad.', 'Decent.', 'Getting There.', 'Room to Grow.', 'Shows Promise.'],
  C: [],
  D: [],
};

/** 1st-miss variants (world-space, small, gray). */
export const WHIFF_VARIANTS = ['WHIFF', 'MISS', 'NOPE', 'SWING AND A MISS', 'AIR BALL'];

/** 2nd-miss variants (screen-space, red). */
export const BOO_VARIANTS = ['BOO!', 'YIKES', 'COME ON', 'REALLY?', 'DO BETTER'];
